import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { Icon } from 'src/common/components/icon';
import { useOpenModal } from 'src/common/components/next-modal';
import { DATE_TIME_FORMAT } from 'src/common/const';
import { useOpenUpgradeAiModal } from 'src/components/select-upgrade-plan/upgrade-ai';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { Modals } from 'src/modals';
import { useIsAiPlan, useSpaceCapacity } from 'src/services/capacity';
import { OrderListSupport, useSetting } from '../common';
import { Option } from '../option';
import { FlowusAIHistory } from './ai-history';

export const SpaceInfoAI: FC = () => {
  const { t } = useAppTranslation();
  const spaceCapacity = useSpaceCapacity();
  const openModal = useOpenModal();
  const isAiPlan = useIsAiPlan();
  const openUpgradeAiModal = useOpenUpgradeAiModal();
  const { enableAI } = useEnableAI();
  const isProSpace = useIsProSpace();
  const { toUpgradeAi } = useSetting();

  const openAiOrderList = useCallback(() => {
    openModal.modal({
      modalId: Modals.ORDER_LIST_SUPPORT,
      content: () => (
        <OrderListSupport
          title={t('common.product_ai_pay_title')}
          subTitle={t('components.space_info_setting.space_info_ai.buy_product', {
            name: t('common.product_ai_pay_title'),
          })}
          orderType="aiUsage"
          openUpgradeModal={openUpgradeAiModal}
        />
      ),
    });
  }, [openModal, openUpgradeAiModal, t]);

  const openHistory = () => {
    openModal.modal({
      content: () => <FlowusAIHistory />,
    });
  };

  const aiPlanButton = useMemo(() => {
    if (spaceCapacity.monthlyEndDate) {
      return (
        <span className="animate-click" onClick={() => openAiOrderList()}>
          {t('components.space_info_setting.space_info_ai.expiry_date')}{' '}
          {dayjs(spaceCapacity.monthlyEndDate).format(DATE_TIME_FORMAT)}
        </span>
      );
    }
    return null;
  }, [openAiOrderList, spaceCapacity.monthlyEndDate, t]);

  if (__BUILD_IN__) {
    const { baseModel, pdf, additional } = spaceCapacity;

    const monthUsed = baseModel.monthUsed + pdf.monthUsed;

    const baseModelRest = isAiPlan
      ? baseModel.monthLimit - baseModel.monthUsed
      : baseModel.dayLimit - baseModel.todayUsed;

    const pdfRest = isAiPlan ? pdf.dayLimit - pdf.todayUsed : pdf.monthLimit - pdf.monthUsed;
    return (
      <>
        <Option title={t('common.product_ai_title_buildin')} rightPart={isProSpace ? '' : ''} />
        {!isProSpace && (
          <div className="flex items-center text-t3 h-9">
            <span className="mr-4">
              {t('components.space_info_setting.space_info_ai.used_count', {
                used: monthUsed,
                total: pdfRest + baseModelRest,
              })}
            </span>
            <button className="ml-auto mr-[5px] flex items-center" onClick={openHistory}>
              <span className="text-t3 text-grey3">
                {t('components.space_info_setting.space_info_ai.view_details')}
              </span>
              <Icon name="IcArrowUnfold" size="xxxsmall" className="text-grey3" />
            </button>
          </div>
        )}

        <div className="bg-grey9 rounded py-2 px-4 text-t2 text-grey3">
          <div className="h-7 flex justify-between items-center">
            <span className="">
              {t('components.space_info_setting.space_info_ai.base_requests')}： &nbsp;
              {`${baseModel.monthUsed}/${baseModel.monthLimit}`}
            </span>
          </div>
          {/* <div className="h-7 flex justify-between items-center">
            <span>
              {t('components.space_info_setting.space_info_ai.additional_requests')}：
              {t('components.space_info_setting.space_info_ai.total')} {additional}
            </span>
          </div> */}
          {/* <div className="h-7 flex justify-between items-center">
            <span>
              阅读 PDF：{isAiPlan ? '每日' : '每月'}
              {isAiPlan ? `${pdf.todayUsed}/${pdf.dayLimit}` : `${pdf.monthUsed}/${pdf.monthLimit}`}
            </span>
          </div> */}
        </div>

        <div className="bg-grey9 rounded py-2 px-4 text-t2 text-grey3 mt-2.5 flex justify-between">
          <span>{t('components.space_info_setting.space_info_ai.additional_benefits')}</span>
          <a
            className="underline ml-2 text-t3 text-active_color cursor-pointer"
            target="_blank"
            onClick={() => toUpgradeAi()}
          >
            {t('common.buy_query_quota')}
          </a>
        </div>
      </>
    );
  }

  return (
    <>
      <Option
        title={t('common.product_ai_title')}
        rightPart={isProSpace ? t('common.unlimited_times') : ''}
      />
      {!isProSpace && (
        <div className="flex items-center text-t3 h-9">
          <span className="mr-4">
            {t('common.ai_usage_status', {
              used: spaceCapacity.usage,
              total: isProSpace ? t('common.unlimited_times') : spaceCapacity.aiRestUsage,
            })}
          </span>
          <button
            hidden={!enableAI}
            className="ml-auto mr-[5px] flex items-center"
            onClick={openHistory}
          >
            <span className="text-t3 text-grey3">
              {t('components.space_info_setting.space_info_ai.view_details')}
            </span>
            <Icon name="IcArrowUnfold" size="xxxsmall" className="text-grey3" />
          </button>
        </div>
      )}

      {!isProSpace && (
        <div className="bg-grey9 rounded py-2 px-4 text-t2 text-grey3 mt-2.5 flex justify-between">
          <div className="space-x-1">
            <span>{t('common.product_ai_pay_title')}</span>
            <span
              className="text-active_color underline underline-offset-2 animate-click"
              onClick={() => openUpgradeAiModal()}
            >
              {t('common.buy_query_quota')}
            </span>
          </div>
          {aiPlanButton}
        </div>
      )}
    </>
  );
};
